import React, {cloneElement, useEffect, useState} from "react";
import {
    CheckList,
    DeliveryDone,
    DeliveryTrack2,
    FastDelivery,
    LogoAsBg,
    OnWay,
    SectionTitleLeftLine,
    SectionTitleRightLine,
    ServicesIcon,
    TrackingActiveLine,
    TrackingGrayLine,
    TrackingRedLine,
    Xmark
} from "../assets/icons";
import {useLanguage} from "../hooks/LanguageContext";
import {useParams} from "react-router-dom";
import apiService from "../services/apiService";

const TrackingPage = () => {
    const [data, setData] = useState(null);
    const { direction } = useLanguage();
    const { code } = useParams();
    const [waybillId,setWaybillId] = useState(null)
    const [steps, setSteps] = useState([
        { id: 1, title_en: "Booked", title_ar: "تم الحجز", subtitle_ar: "المرحلة 1",  subtitle_en: "Phase 1",desc_en: "The booking for your vehicle has been confirmed, and transport details are being prepared.", desc_ar: "تم تأكيد حجز شحن مركبتك، وجاري تجهيز تفاصيل النقل.", status: "completed", icon : <CheckList />},
        { id: 2, title_en: "Ready to Ship", title_ar: "جاهزة للشحن", subtitle_ar: "المرحلة 2", subtitle_en: "Phase 2", desc_en: "Your vehicle is ready for shipment, it will be loaded onto the transporter soon.", desc_ar: "مركبتك جاهزة للشحن، وسيتم تحميلها على الناقلة قريبًا.", status: "pending", code: "41004" , icon : <DeliveryTrack2 /> },
        { id: 3, title_en: "In Transit", title_ar: "في الطريق", subtitle_ar: "المرحلة 3",  subtitle_en: "Phase 3",desc_en: "Your vehicle is now on its way to its destination.", desc_ar: "مركبتك الآن في الطريق إلى وجهتها.", status: "pending", code: "41006" , icon : <OnWay />},
        { id: 4, title_en: "Arrived", title_ar: "وصلت", subtitle_ar: "المرحلة 4", subtitle_en: "Phase 4", desc_en: "Thank you. We are pleased to serve you. You have arrived.", desc_ar: " شكراً لكم سعدنا بخدمتكم وصلت سيارتكم.", status: "pending", code: "41007", icon : <FastDelivery /> },
        { id: 5, title_en: "Delivered", title_ar: "تم التسليم", subtitle_ar: "المرحلة 5", subtitle_en: "Phase 5", desc_en: "Your vehicle has been successfully delivered. Thank you for choosing AlFayez Company.", desc_ar: "تم تسليم مركبتك بنجاح. شكرًا لاختيارك شركة الفايز.", status: "pending", code: "41008", icon : <DeliveryDone />  }
    ]);
    const [activeStep,setActiveStep] = useState(null);
    useEffect(() => {
        if (!code) return;

        apiService.getShipmentTracking(code)
            .then((response) => {
                const { waybill_id, status, created_date, waybill_load_date, updated_date, waybill_delivery_date } = response.data;
                if (!waybill_id) return;

                setData(response.data);
                setWaybillId(waybill_id);

                const systemCode = status?.system_code;
                let updatedSteps = [];

                if ( parseInt(systemCode)  === 41005) {
                    updatedSteps = [
                        { id: 1, title_en: "Booked", title_ar: "تم الحجز", subtitle_ar: "المرحلة 1", subtitle_en: "Phase 1", desc_en: "The booking for your vehicle has been confirmed, and transport details are being prepared.", desc_ar: "تم تأكيد حجز شحن مركبتك، وجاري تجهيز تفاصيل النقل.", status: "completed", icon: <CheckList /> },
                        { id: 6, title_en: "Cancelled", title_ar: "ملغاة", subtitle_ar: "المرحلة 2", subtitle_en: "Phase 2", desc_en: "Your booking has been cancelled. Please contact support for further assistance.", desc_ar: "تم إلغاء الحجز الخاص بك. يرجى الاتصال بالدعم لمزيد من المساعدة.", status: "active", icon: <Xmark /> },
                    ];
                } else {
                     updatedSteps = steps.map((step) => {
                        if (step.id === 1) return step;

                        return {
                            ...step,
                            status: step.code === systemCode ? "active"
                                : step.code < systemCode ? "completed"
                                    : "pending",
                        };
                    });
                }

                setSteps(updatedSteps);

                const activeStep = updatedSteps.find((step) => step.status === "active");
                if (activeStep) {
                    const dateMap = {
                        1: created_date,
                        2: waybill_load_date,
                        3: waybill_load_date,
                        4: updated_date,
                        5: waybill_delivery_date,
                        6: updated_date
                    };
                    setActiveStep({
                        ...activeStep,
                        date: dateMap[activeStep.id] ? new Date(dateMap[activeStep.id]).toISOString().split("T")[0] : null
                    });
                } else {
                    setActiveStep(null);
                }
            })
            .catch((err) => {
                console.error("Error fetching shipment tracking:", err);
            });
    }, [code]);


    const getStepStatusClass = (status) => {
        switch (status) {
            case "pending":
                return "bg-white text-grayscale-400 border-4 border-grayscale-400";
            case "completed":
                return "bg-primary-700 text-white";
            case "active":
                return "bg-white text-primary-700 border-4 border-primary-700";
            default:
                return "";
        }
    };

    const isPreviousStepCompleted = (index) => {
        return steps[index - 1] && steps[index - 1].status === "completed";
    };

    const isPreviousStepActive = (index) => {
        return steps[index - 1] && steps[index - 1].status === "active";
    };

    return (
        <div className="relative md:mt-60">
            {/* Header Section */}
            <div className="flex justify-center items-center flex-col">
                <div className="flex items-center justify-center gap-4">
                    <SectionTitleRightLine />
                    <span className="text-grayscale-600 text-xl md:text-3xl font-bold">
                        {direction === "rtl" ? "تتبع سيارتك" : "Track your vehicle"}
                    </span>
                    <SectionTitleLeftLine />
                </div>
                {data?.truck?.truck_plate_no && (
                    <div className="flex items-center justify-center gap-4 mt-2 md:mt-6">
                        <SectionTitleRightLine className="hidden md:block" />
                        <h3 className="text-grayscale-700 text-md md:text-2xl font-semibold">
                            {direction === "rtl" ? "السيارة محملة على الشاحنة رقم" : "The car is loaded onto truck number"}:{" "}
                            <span className="text-primary-700 font-bold">{data?.truck?.truck_plate_no}</span>
                        </h3>
                        <SectionTitleLeftLine className="hidden md:block" />
                    </div>
                )}
                {data?.loc_from?.system_code_name_ar && (
                    <div className="flex items-center justify-center gap-4 mt-2 md:mt-6">
                        <SectionTitleRightLine className="hidden md:block" />
                        <h3 className="text-grayscale-700 text-md md:text-2xl font-semibold">
                            {direction === "rtl" ? "فرع الانطلاق" : "Departure Branch"}:{" "}
                            <span className="text-primary-700 font-bold">
                    {direction === "rtl"
                        ? data?.loc_from?.system_code_name_ar
                        : data?.loc_from?.system_code_name_en}
                </span>
                        </h3>
                        <SectionTitleLeftLine className="hidden md:block" />
                    </div>
                )}
                <div className=" mt-6 md:mt-8   mx-auto">
                    <a
                        href={`https://app.alfayzgroup.com/external/waybills/cars/show/${waybillId} `}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-primary-700 text-white px-4 md:px-6 py-1 md:py-3 rounded-full text-lg font-medium shadow-md hover:bg-primary-800 transition-all"
                    >
                        {direction === "rtl" ? "المزيد من التفاصيل" : "More Details"}
                    </a>
                </div>
            </div>

            <div className="absolute right-0 top-0 hidden md:flex">
                <ServicesIcon />
                <ServicesIcon />
            </div>

            <LogoAsBg
                className={`absolute  z-[999] overflow-hidden -right-28`}
            />

            <section className="bg-white w-full h-full mt-[40px] md:mt-[100px] z-0">
                <div className="container flex flex-col sm:flex-row justify-center items-center">
                    {steps.map((step, index) => {
                        const isCompleted = step.status === "completed";
                        const isActive = step.status === "active";
                        const isFirstStep = index === 0;
                        return (
                            <div key={step.id} className="flex sm:flex-row flex-col items-center">
                                {!isFirstStep && (
                                    <div className="flex flex-col items-center gap-y-8 sm:gap-y-4 sm:mb-28 mb-8">
                                        <div
                                            className={`lg:w-[130px] w-[70px] h-[40px] lg:h-[50px] whitespace-nowrap text-sm md:text-md ${
                                                isPreviousStepCompleted(index)
                                                    ? "bg-primary-700 text-white"
                                                    : isPreviousStepActive(index)
                                                        ? "border-2 border-primary-700 text-primary-700"
                                                        : "border-2 border-grayscale-400 text-grayscale-400"
                                            } rounded-lg inline-flex items-center justify-center font-bold`}
                                        >
                            <span>
                                {direction === 'rtl'
                                    ? isPreviousStepCompleted(index)
                                        ? "مكتمل"
                                        : isPreviousStepActive(index)
                                            ? "قيد التنفيذ"
                                            : "قيد الانتظار"
                                    : isPreviousStepCompleted(index)
                                        ? "Completed"
                                        : isPreviousStepActive(index)
                                            ? "In Progress"
                                            : "Pending"}
                            </span>
                                        </div>

                                        <div
                                            className={`w-full lg:max-w-[182px] max-w-[80px] transition-transform duration-300 ${
                                                direction === 'ltr' ? 'sm:rotate-180 -rotate-90' : 'rotate-90 sm:rotate-0'
                                            }`}
                                        >
                                            {isPreviousStepCompleted(index) ? (
                                                <TrackingRedLine />
                                            ) : isPreviousStepActive(index) ? (
                                                <TrackingActiveLine />
                                            ) : (
                                                <TrackingGrayLine />
                                            )}
                                        </div>
                                    </div>
                                )}
                                <div className="flex flex-col items-center">
                                    <div
                                        className={`flex items-center justify-center lg:w-[100px] lg:h-[100px] w-[70px] h-[70px] rounded-full ${getStepStatusClass(
                                            step.status
                                        )}`}
                                    >
                                        {step.icon &&
                                            cloneElement(step.icon, {
                                                className: `${
                                                    isCompleted
                                                        ? "text-white"
                                                        : isActive
                                                            ? "text-primary-700"
                                                            : "text-grayscale-400"
                                                } w-[30px] h-[30px] md:w-[40px] md:h-[40px] lg:w-[60px] lg:h-[60px]  `,
                                            })}
                                    </div>
                                    <div className="flex flex-col justify-center items-center">
                                        <div>{direction === "rtl" ? step.subtitle_ar : step.subtitle_en}</div>
                                        <div
                                            className={`${isCompleted || isActive ? "font-bold" : ""} ${
                                                isActive ? "text-primary-700" : ""
                                            }`}
                                        >
                                            {direction === "rtl" ? step.title_ar : step.title_en}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>




                {activeStep &&   <div className="container max-w-screen-md lg:max-w-screen-xl mx-auto md:h-[300px] h-[230px] mt-8 mb-8 rounded-[20px] bg-bg-darken shadow-serviceBoxShadow px-4 md:px-28 flex items-center gap-[19px]">
                    <div className="flex flex-col">
                        <h3 className="font-bold text-sm md:text-3xl text-grayscale-600 whitespace-nowrap">
                            {activeStep?.id + ". " } {direction === "rtl" ? activeStep?.title_ar : activeStep?.title_en}
                        </h3>
                        <br />
                        <p
                            className={`w-full leading-[23.52px] font-bold  text-sm md:text-2xl ${
                                direction === "rtl" ? "text-right" : "text-left"
                            } text-grayscale-500`}
                        >
                            {direction === "rtl" ? activeStep.desc_ar : activeStep.desc_en}
                        </p>
                        <p
                            className={`mt-2 w-full leading-[23.52px] font-bold text-sm md:text-2xl ${
                                direction === "rtl" ? "text-right" : "text-left"
                            } text-primary-700`}
                        >
                            {activeStep?.date && <>
                                {direction === "ltr" ?  "Date" : "بتاريخ" }:  {activeStep.date}
                            </>}
                        </p>
                    </div>
                    <div className={direction === 'rtl' ? 'mr-auto' : 'ml-auto'}>
                        {activeStep?.icon &&
                            cloneElement(activeStep.icon, {
                                className: `text-primary-700 w-[60px] h-[60px] sm:w-[100px] sm:h-[100px] lg:w-[180px] lg:h-[130px] xl:w-[290px] xl:h-[230px] `,
                            })}
                    </div>
                </div>}

            </section>
        </div>
    );
};

export default TrackingPage;
